import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { trackEvent } from '../../lib/track';

/**
 * MobileStickyCTA — always-present "Book a Call" bar on phones only.
 *
 * Rationale: on desktop the header CTA is always in view. On mobile the header
 * CTA is behind the hamburger, so we surface a persistent, high-contrast primary
 * action. Rotates through 4 short pitches so the bar stays fresh and reminds
 * visitors that Jo does more than the Pro Plan.
 *
 * Hidden on routes where booking is already the focus (or irrelevant): /book,
 * /booked, /portal/*, /admin, /training-terms.
 */
const HIDE_PREFIXES = ['/book', '/booked', '/portal', '/admin', '/training-terms'];

// Each pitch: headline + subline + CTA label. Kept tight so it always fits on
// a 360px viewport without truncation.
const PITCHES = [
  { headline: 'The Pro Plan',            sub: '£280/wk · 4-week minimum',           cta: "Check if we're a fit" },
  { headline: 'Book or audiobook?',      sub: 'Jo takes bespoke publishing work',  cta: "Check if we're a fit" },
  { headline: 'Podcast, done for you',   sub: 'One recording, twelve outputs',     cta: "Check if we're a fit" },
  { headline: 'Website or app build?',   sub: 'Small number of custom builds/yr',  cta: "Check if we're a fit" },
  { headline: 'Not sure what you need?', sub: '30-min fit call. No hard sell.',    cta: "Check if we're a fit" },
];

const ROTATE_MS = 5000;

export default function MobileStickyCTA() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    // Show as soon as the user starts scrolling. Threshold intentionally
    // small so the button reads as "always available on scroll" rather
    // than something that appears only after a full hero pass.
    const onScroll = () => setVisible(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  const shouldHide = HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));

  // Reserve safe-area at the bottom of the page while active so nothing hides
  // behind the bar.
  useEffect(() => {
    const cls = 'has-mobile-sticky-cta';
    if (shouldHide) {
      document.body.classList.remove(cls);
    } else {
      document.body.classList.add(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [shouldHide]);

  // Rotate the pitch every ROTATE_MS. Pause when the tab is hidden to avoid
  // waking a phone unnecessarily.
  useEffect(() => {
    if (shouldHide || !visible) return undefined;
    const tick = () => {
      if (document.hidden) return;
      setFade(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % PITCHES.length);
        setFade(true);
      }, 220);
    };
    const t = setInterval(tick, ROTATE_MS);
    return () => clearInterval(t);
  }, [shouldHide, visible]);

  // Track an impression when a new pitch becomes visible. One event per (label,
  // path) transition so we don't spam if a user idles on the same page.
  useEffect(() => {
    if (shouldHide || !visible) return;
    trackEvent('sticky_impression', PITCHES[idx].headline, { pitch_idx: idx });
  }, [idx, visible, shouldHide]);

  if (shouldHide) return null;

  const pitch = PITCHES[idx];

  return (
    <div
      className={`sm:hidden fixed left-0 right-0 bottom-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 pointer-events-none transition-transform duration-300 ${
        visible ? 'translate-y-0' : 'translate-y-[120%]'
      }`}
      data-testid="mobile-sticky-cta"
      aria-hidden={!visible}
    >
      <div className="pointer-events-auto rounded-[14px] shadow-[0_-6px_24px_rgba(30,42,68,0.15)] bg-white border border-[#E2E8F0] flex items-center gap-2 p-2">
        <div
          className={`flex-1 min-w-0 pl-2 transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'}`}
          data-testid="mobile-sticky-cta-pitch"
        >
          <p className="text-[13px] font-semibold text-[#1E2A44] leading-tight truncate">{pitch.headline}</p>
          <p className="text-[11px] text-[#64748B] leading-tight truncate">{pitch.sub}</p>
        </div>
        <Link
          to="/book/"
          onClick={() => trackEvent('sticky_click', pitch.headline, { pitch_idx: idx, cta: pitch.cta })}
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#BC516F] hover:bg-[#9A3A54] text-white font-semibold text-sm px-4 py-3 rounded-[10px] shadow-[0_6px_18px_rgba(188,81,111,0.35)] transition"
          data-testid="mobile-sticky-cta-book"
        >
          <span className={`transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'}`}>{pitch.cta}</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
